/* eslint-disable no-console */
/**
 * GLPFlo client-side observability bootstrap.
 *
 * MUST be the very first import in src/index.js so Sentry + PostHog are armed
 * before any React component renders.
 *
 * Free-tier compliance posture:
 *  • Sentry  - all events run through `beforeSend` to strip PHI
 *  • PostHog - free tier has NO BAA. Autocapture / session replay are OFF,
 *    URLs are sanitised (no query strings), user ids are hashed before
 *    `identify`, and `before_send` strips known PHI keys.
 */
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

// ---------------------------------------------------------------------------
// PHI / PII scrubbing helpers (shared by Sentry + PostHog)
// ---------------------------------------------------------------------------
const PHI_FIELDS = new Set([
  "email", "email_address", "user_email", "patient_email",
  "name", "first_name", "last_name", "full_name", "patient_name",
  "phone", "phone_number", "mobile",
  "address", "street_address", "address_street", "address_city",
  "address_state", "address_zip", "city", "state", "zip", "zip_code",
  "postal_code", "date_of_birth", "dob", "birthdate",
  "ssn", "ssn_last4", "social_security_number",
  "patient_id", "medical_record_number", "mrn",
  "prescription", "medication", "drug_name", "dosage", "directions",
  "diagnosis", "icd_code", "diagnosis_code",
  "insurance_id", "policy_number",
  "password", "password_hash", "auth_token", "access_token",
  "refresh_token", "api_key", "api_secret", "secret_key",
  "session_id", "session_token", "authorization", "cookie",
  "weight", "weight_lbs", "height", "bmi", "blood_pressure",
  "allergies", "medical_history", "current_medications",
  "emergency_contact_name", "emergency_contact_phone",
  "tracking_number", "authnet_customer_id", "authnet_subscription_id",
]);

const REDACT = "[REDACTED]";
const EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /\b(?:\+?1[\s\-.]?)?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}\b/g;
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
const DOB_RE = /\b\d{1,2}[/\-]\d{1,2}[/\-]\d{2,4}\b/g;

function scrubText(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(EMAIL_RE, REDACT)
    .replace(PHONE_RE, REDACT)
    .replace(SSN_RE, REDACT)
    .replace(DOB_RE, REDACT);
}

function scrubValue(value, depth = 0) {
  if (depth > 8) return REDACT;
  if (value == null) return value;
  if (typeof value === "string") return scrubText(value);
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (typeof k === "string" && PHI_FIELDS.has(k.toLowerCase())) {
        out[k] = REDACT;
      } else {
        out[k] = scrubValue(v, depth + 1);
      }
    }
    return out;
  }
  return value;
}

export function sanitizeUrl(url) {
  if (typeof url !== "string") return url;
  try {
    const u = new URL(url, window.location.origin);
    // Drop query string + fragment - they regularly contain patient_id, token, etc.
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.replace(/[?#].*$/, "");
  }
}

// One-way hash for user IDs sent to PostHog. Uses Web Crypto SHA-256 + a
// build-time salt so the same internal user_id always maps to the same
// distinct_id without ever exposing the raw value.
async function hashUserId(rawId) {
  if (!rawId) return "anonymous";
  const salt = process.env.REACT_APP_POSTHOG_USER_ID_SALT || "glpflo-default";
  const data = new TextEncoder().encode(`${salt}:${rawId}`);
  try {
    const buf = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32);
  } catch {
    // Insecure context fallback - still better than raw email.
    return `u_${String(rawId).length}_${String(rawId).slice(-4)}`;
  }
}

// ---------------------------------------------------------------------------
// Sentry
// ---------------------------------------------------------------------------
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";
const SENTRY_ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || "production";

if (SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENV,
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      tracesSampleRate: parseFloat(
        process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.01",
      ),
      sendDefaultPii: false,
      attachStacktrace: true,
      // GlitchTip-specific: it doesn't support session events, so disable
      // session tracking to stop the SDK from posting empty session pings.
      autoSessionTracking: false,
      // We intentionally omit browserTracingIntegration() because GlitchTip
      // has limited transaction support; we send only error events.
      integrations: [],
      beforeSend(event) {
        try {
          if (event.request) {
            if (event.request.url) event.request.url = sanitizeUrl(event.request.url);
            if (event.request.query_string) event.request.query_string = REDACT;
            if (event.request.data) event.request.data = scrubValue(event.request.data);
            if (event.request.headers) event.request.headers = scrubValue(event.request.headers);
          }
          if (event.user) {
            for (const k of ["email", "username", "ip_address", "name"]) {
              if (event.user[k]) event.user[k] = REDACT;
            }
          }
          if (event.breadcrumbs) {
            event.breadcrumbs = event.breadcrumbs.map((bc) => ({
              ...bc,
              message: scrubText(bc.message),
              data: bc.data ? scrubValue(bc.data) : bc.data,
            }));
          }
          if (event.exception?.values) {
            event.exception.values.forEach((ex) => {
              if (ex.value) ex.value = scrubText(ex.value);
            });
          }
          for (const bucket of ["contexts", "extra", "tags"]) {
            if (event[bucket]) event[bucket] = scrubValue(event[bucket]);
          }
        } catch (e) {
          console.error("[Sentry] scrubber error", e);
        }
        return event;
      },
    });
    console.log(`[Sentry] initialised (env=${SENTRY_ENV})`);
  } catch (e) {
    console.error("[Sentry] init failed", e);
  }
} else {
  console.info("[Sentry] REACT_APP_SENTRY_DSN not set - error tracking disabled");
}

// ---------------------------------------------------------------------------
// PostHog (HIPAA-aware: no BAA on free tier)
// Gated by cookie consent — initialised only when user explicitly opts in.
// ---------------------------------------------------------------------------
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
let posthogReady = false;

function hasAnalyticsConsentRaw() {
  try {
    const raw = localStorage.getItem("glpflo_cookie_consent_v1");
    if (!raw) return false;
    return !!JSON.parse(raw)?.analytics;
  } catch {
    return false;
  }
}

function initPostHog() {
  if (posthogReady || !POSTHOG_KEY) return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // HIPAA posture - turn off everything that auto-captures user content.
      autocapture: false,
      capture_pageview: false, // we send sanitised pageviews manually
      capture_pageleave: true,
      disable_session_recording: true,
      session_recording: { recordCanvas: false, maskAllInputs: true },
      mask_all_text: true,
      mask_all_element_attributes: true,
      capture_performance: false,
      person_profiles: "identified_only",
      respect_dnt: true,
      ip: false,
      property_blacklist: [
        "$ip", "$initial_referrer", "$referrer",
      ],
      sanitize_properties: (properties /* event */) => {
        try {
          const safe = scrubValue(properties || {});
          if (safe.$current_url) safe.$current_url = sanitizeUrl(safe.$current_url);
          if (safe.$pathname) safe.$pathname = sanitizeUrl(safe.$pathname);
          delete safe.$ip;
          delete safe.$referrer;
          delete safe.$initial_referrer;
          return safe;
        } catch (e) {
          console.error("[PostHog] sanitize_properties error", e);
          return {};
        }
      },
      loaded: () => {
        posthogReady = true;
        console.log(`[PostHog] initialised (host=${POSTHOG_HOST})`);
      },
    });
  } catch (e) {
    console.error("[PostHog] init failed", e);
  }
}

if (POSTHOG_KEY) {
  if (hasAnalyticsConsentRaw()) {
    initPostHog();
  } else {
    console.info("[PostHog] waiting for cookie consent");
  }
  // Listen for late grant from the cookie banner.
  if (typeof window !== "undefined") {
    window.addEventListener("glpflo:cookie-consent", (e) => {
      if (e?.detail?.analytics && !posthogReady) initPostHog();
    });
  }
} else {
  console.info("[PostHog] REACT_APP_POSTHOG_KEY not set - analytics disabled");
}

// ---------------------------------------------------------------------------
// Google Analytics 4 (gtag.js) — loaded from public/index.html. This block
// just re-exposes it through the same `analytics` API so app code has one
// call site (fires PostHog + GA4 together). Scrubbed properties are safe
// to forward: GA4 sees no email/name/etc.
// ---------------------------------------------------------------------------
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

function gaReady() {
  return !!GA_ID && typeof window !== "undefined" && typeof window.gtag === "function";
}

function gaSend(command, ...args) {
  if (!gaReady()) return;
  try { window.gtag(command, ...args); } catch (e) {
    console.error("[GA4] send failed", e);
  }
}

// ---------------------------------------------------------------------------
// Public helpers used by app code
// ---------------------------------------------------------------------------
export const analytics = {
  /** Sanitised page-view. Call from a router effect. */
  page(pathname) {
    const path = sanitizeUrl(pathname || window.location.pathname);
    if (gaReady()) {
      gaSend("event", "page_view", {
        page_path: path,
        page_location: sanitizeUrl(window.location.href),
        page_title: typeof document !== "undefined" ? document.title : "",
      });
    }
    if (!posthogReady) return;
    try {
      posthog.capture("$pageview", {
        $current_url: sanitizeUrl(window.location.href),
        $pathname: path,
      });
    } catch (e) {
      console.error("[PostHog] page failed", e);
    }
  },
  /** Identify a logged-in user with a hashed id. NEVER pass raw email/name. */
  async identify(rawUserId, role) {
    if (!rawUserId) return;
    try {
      const distinct = await hashUserId(rawUserId);
      if (gaReady()) {
        gaSend("config", GA_ID, { user_id: distinct });
        gaSend("set", "user_properties", { role: role || "unknown" });
      }
      if (posthogReady) {
        posthog.identify(distinct, { role: role || "unknown" });
      }
    } catch (e) {
      console.error("[analytics] identify failed", e);
    }
  },
  reset() {
    if (gaReady()) {
      gaSend("config", GA_ID, { user_id: undefined });
    }
    if (!posthogReady) return;
    try { posthog.reset(); } catch { /* noop */ }
  },
  /** Capture a non-PHI product event. Properties are scrubbed automatically. */
  capture(eventName, properties) {
    if (!eventName) return;
    const scrubbed = scrubValue(properties || {});
    if (gaReady()) {
      gaSend("event", eventName, scrubbed);
    }
    if (!posthogReady) return;
    try {
      posthog.capture(eventName, scrubbed);
    } catch (e) {
      console.error("[PostHog] capture failed", e);
    }
  },
};

export { Sentry };
